import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowUpRight, Check, Mail, Phone, MessageCircle, Loader2 } from "lucide-react";

import PageHero from "@/components/PageHero";
import { FadeUp } from "@/components/Motion";
import SeoHead from "@/components/SeoHead";
import { useSite } from "@/contexts/SiteContext";
import { publicApi, ApiError } from "@/lib/api";

/**
 * Contact form.
 *
 * Deliberately short: name, mobile, email and message. Every additional field on
 * an enquiry form costs completions, and the qualifying detail this page used to
 * ask for — client type, property type, budget, timeframe — is better established
 * in the first conversation than guessed at by a visitor.
 *
 * Submissions go to POST /api/public/enquiries, which validates everything again
 * server-side. The client-side checks here exist to give immediate feedback, not
 * to protect anything — a form that only validates in the browser validates
 * nothing.
 *
 * Three anti-spam measures cooperate with the server: a honeypot field named by
 * site settings, referral and campaign parameters captured from the URL, and an
 * optional reCAPTCHA v3 token when a site key is configured.
 */

const INITIAL = { fullName: "", mobile: "", email: "", message: "", consent: false };

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || "";

/** Load reCAPTCHA v3 once, on demand. */
function useRecaptcha(enabled) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!enabled || !RECAPTCHA_SITE_KEY) return undefined;
    if (window.grecaptcha) { setReady(true); return undefined; }

    const script = document.createElement("script");
    script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    script.async = true;
    script.onload = () => setReady(true);
    document.head.appendChild(script);
    return () => { script.onload = null; };
  }, [enabled]);

  return async () => {
    if (!enabled || !RECAPTCHA_SITE_KEY || !ready || !window.grecaptcha) return null;
    try {
      return await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: "contact" });
    } catch (_) {
      // A CAPTCHA failure must not block a genuine enquiry; the server decides
      // what to do with a missing token.
      return null;
    }
  };
}

/** Capture attribution once, on first render, before any client-side navigation. */
function useAttribution() {
  const ref = useRef(null);
  if (ref.current === null) {
    const params = new URLSearchParams(window.location.search);
    const campaign = {};
    ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "gclid", "fbclid"]
      .forEach((key) => {
        const value = params.get(key);
        if (value) campaign[key] = value;
      });

    ref.current = {
      landingPage: window.location.pathname + window.location.search,
      referralSource: document.referrer || "",
      campaignParams: campaign,
    };
  }
  return ref.current;
}

export default function Contact() {
  const { site, forms } = useSite();
  const attribution = useAttribution();
  const getCaptchaToken = useRecaptcha(forms.requireCaptcha);

  const [form, setForm] = useState(INITIAL);
  const [honeypot, setHoneypot] = useState("");
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const set = (key) => (e) =>
    setForm({ ...form, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const validate = () => {
    const next = {};
    if (!form.fullName.trim()) next.fullName = "Full name is required.";
    if (!/^\+?[0-9\s\-()]{7,}$/.test(form.mobile)) next.mobile = "Enter a valid mobile number.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) next.email = "Enter a valid email address.";
    if (!form.message.trim() || form.message.trim().length < 10) {
      next.message = "Please tell us a little more — at least 10 characters.";
    }
    if (!form.consent) next.consent = "Please confirm you agree to be contacted.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) {
      toast.error("Please correct the highlighted fields.");
      return;
    }

    setSubmitting(true);
    try {
      const captchaToken = await getCaptchaToken();

      const response = await publicApi.submitEnquiry({
        ...form,
        ...attribution,
        [forms.honeypotField]: honeypot,
        captchaToken,
      });

      setResult(response);
      toast.success(response.message || "Your enquiry has been received.");
    } catch (err) {
      if (err instanceof ApiError && err.fieldErrors) {
        setErrors(err.fieldErrors);
        toast.error(err.message);
      } else if (err instanceof ApiError && err.isRateLimited) {
        toast.error(err.message, { duration: 8000 });
      } else {
        toast.error(
          err.message ||
            "We could not send your enquiry. Please try again, or email us directly."
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SeoHead pageKey="contact" />

      <PageHero
        eyebrow="Contact"
        chapter="Chapter — Engage"
        lines={["Speak with a", "PIRAC advisor."]}
        blurb="Request a confidential consultation. A member of the advisory team will respond within one working day."
        image="/assets/hero-contact"
        imageAlt="A PIRAC desk overlooking Marina Bay at sunrise — bound volumes on Singapore property, market insights, investment strategy and wealth planning beside a laptop and a branded folio"
      />

      <section className="bg-[#060f1e] py-16 lg:py-24">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-12 gap-8">
          <aside className="col-span-12 lg:col-span-4">
            <FadeUp>
              <div className="p-8 border border-[#1a2940] bg-[#0c1728] space-y-6">
                {site.contactEmail && (
                  <div>
                    <div className="overline mb-2">Email</div>
                    <a
                      href={`mailto:${site.contactEmail}`}
                      className="min-h-11 py-1 -my-1 flex items-center gap-3 text-[#f7f5f0] hover:text-[#cca24d] break-all"
                    >
                      <Mail className="h-4 w-4 shrink-0" strokeWidth={1.5} /> {site.contactEmail}
                    </a>
                  </div>
                )}
                {site.contactPhone && (
                  <div>
                    <div className="overline mb-2">Phone</div>
                    <a
                      href={`tel:${site.contactPhone.replace(/\s/g, "")}`}
                      className="min-h-11 py-1 -my-1 flex items-center gap-3 text-[#f7f5f0] hover:text-[#cca24d]"
                    >
                      <Phone className="h-4 w-4" strokeWidth={1.5} /> {site.contactPhone}
                    </a>
                  </div>
                )}
                {site.whatsappNumber && (
                  <div>
                    <div className="overline mb-2">WhatsApp</div>
                    <a
                      href={`https://wa.me/${site.whatsappNumber.replace(/\D/g, "")}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="min-h-11 py-1 -my-1 flex items-center gap-3 text-[#f7f5f0] hover:text-[#cca24d]"
                    >
                      <MessageCircle className="h-4 w-4" strokeWidth={1.5} /> Chat with PIRAC
                    </a>
                  </div>
                )}
                {site.officeAddress && (
                  <div>
                    <div className="overline mb-2">Office</div>
                    <p className="text-sm text-[#d8d8d8] leading-relaxed whitespace-pre-line">
                      {site.officeAddress}
                    </p>
                  </div>
                )}
                <div className="pt-6 border-t border-[#1a2940] text-xs text-[#9ca3af] leading-relaxed">
                  Consultations are held in person at our Singapore office or via secure video call.
                  All enquiries are treated in strict confidence.
                </div>
              </div>
            </FadeUp>
          </aside>

          <div className="col-span-12 lg:col-span-7 lg:col-start-6">
            {result ? (
              <FadeUp>
                <div className="p-10 border border-[#cca24d]/50 bg-[#0c1728]">
                  <div className="w-12 h-12 flex items-center justify-center border border-[#cca24d]">
                    <Check className="h-5 w-5 text-[#cca24d]" strokeWidth={1.5} />
                  </div>
                  <h2 className="mt-6 font-serif text-3xl md:text-4xl text-[#f7f5f0]">
                    Thank you for contacting PIRAC.
                  </h2>
                  <p className="mt-4 text-[#d8d8d8] max-w-xl leading-relaxed">
                    {result.message ||
                      "Your enquiry has been received and our advisory team will be in touch shortly."}
                  </p>
                  <p className="mt-3 text-sm text-[#9ca3af]">
                    A confirmation has been sent to {form.email}.
                  </p>
                  <button
                    type="button"
                    onClick={() => { setForm(INITIAL); setResult(null); setErrors({}); }}
                    className="mt-8 inline-flex items-center gap-2 text-sm text-[#cca24d] editorial-link"
                  >
                    Submit another enquiry <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={1.5} />
                  </button>
                </div>
              </FadeUp>
            ) : (
              <form onSubmit={onSubmit} className="space-y-6" data-testid="contact-form" noValidate>
                {/*
                  Honeypot. Hidden from people by position and aria-hidden rather
                  than display:none, which some bots specifically check for.
                */}
                <div
                  aria-hidden="true"
                  style={{
                    position: "absolute", left: "-9999px", top: "auto",
                    width: "1px", height: "1px", overflow: "hidden",
                  }}
                >
                  <label htmlFor={forms.honeypotField}>Do not fill this in</label>
                  <input
                    id={forms.honeypotField}
                    name={forms.honeypotField}
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={honeypot}
                    onChange={(e) => setHoneypot(e.target.value)}
                  />
                </div>

                <Field label="Name" err={errors.fullName} required>
                  <input
                    data-testid="contact-fullName"
                    value={form.fullName}
                    onChange={set("fullName")}
                    autoComplete="name"
                    className="dark-input"
                  />
                </Field>

                <div className="grid md:grid-cols-2 gap-6">
                  <Field label="Mobile" err={errors.mobile} required>
                    <input
                      data-testid="contact-mobile"
                      value={form.mobile}
                      onChange={set("mobile")}
                      autoComplete="tel"
                      inputMode="tel"
                      className="dark-input"
                      placeholder="+65 "
                    />
                  </Field>
                  <Field label="Email" err={errors.email} required>
                    <input
                      type="email"
                      data-testid="contact-email"
                      value={form.email}
                      onChange={set("email")}
                      autoComplete="email"
                      className="dark-input"
                    />
                  </Field>
                </div>

                <Field label="Message" err={errors.message} required>
                  <textarea
                    data-testid="contact-message"
                    value={form.message}
                    onChange={set("message")}
                    rows={7}
                    className="dark-input resize-none"
                    placeholder="Tell us a little about what you're planning. This helps us route your enquiry to the right advisor."
                  />
                </Field>

                <div className="flex items-start gap-3 pt-2">
                  <input
                    id="consent"
                    type="checkbox"
                    data-testid="contact-consent"
                    checked={form.consent}
                    onChange={set("consent")}
                    className="mt-1 h-4 w-4 accent-[#cca24d]"
                  />
                  <label htmlFor="consent" className="text-sm text-[#d8d8d8] leading-relaxed">
                    I consent to PIRAC contacting me regarding this enquiry, and I have read the{" "}
                    <a href="/privacy-policy" className="text-[#cca24d] editorial-link">
                      privacy policy
                    </a>
                    .
                    {errors.consent && (
                      <span className="block text-[#ef4444] text-xs mt-1">{errors.consent}</span>
                    )}
                  </label>
                </div>

                <button
                  type="submit"
                  data-testid="contact-submit"
                  disabled={submitting}
                  className="group inline-flex items-center gap-3 px-8 h-14 bg-[#cca24d] text-[#060f1e]
                    text-sm font-medium tracking-wide hover:bg-[#e5c98b] transition-colors
                    disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} /> Sending
                    </>
                  ) : (
                    <>
                      Submit Enquiry
                      <ArrowUpRight
                        className="h-4 w-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1"
                        strokeWidth={1.5}
                      />
                    </>
                  )}
                </button>

                {forms.requireCaptcha && RECAPTCHA_SITE_KEY && (
                  <p className="text-[11px] text-[#6b7280] leading-relaxed">
                    This site is protected by reCAPTCHA and the Google{" "}
                    <a
                      href="https://policies.google.com/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline underline-offset-2 hover:text-[#cca24d]"
                    >
                      Privacy Policy
                    </a>{" "}
                    and{" "}
                    <a
                      href="https://policies.google.com/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline underline-offset-2 hover:text-[#cca24d]"
                    >
                      Terms of Service
                    </a>{" "}
                    apply.
                  </p>
                )}
              </form>
            )}
          </div>
        </div>
      </section>

      <style>{`
        .dark-input {
          width: 100%;
          background: #060f1e;
          border: 1px solid #1a2940;
          color: #f7f5f0;
          padding: 0.85rem 1rem;
          font-family: 'DM Sans', sans-serif;
          font-size: 0.95rem;
          border-radius: 2px;
          transition: border-color 200ms ease, box-shadow 200ms ease;
        }
        .dark-input:focus { outline: none; border-color: #cca24d; box-shadow: 0 0 0 3px rgba(204,162,77,0.15); }
      `}</style>
    </>
  );
}

function Field({ label, err, required, children }) {
  return (
    <label className="block">
      <span className="overline block mb-2 text-[#9ca3af]">
        {label}
        {required && <span className="text-[#ef4444] ml-1">*</span>}
      </span>
      {children}
      {err && <span className="block text-[#ef4444] text-xs mt-1">{err}</span>}
    </label>
  );
}
